import {useContext, useEffect, useLayoutEffect, useState} from "react";
import {AppContextContext, AuthServiceContext, ClientNotificationServiceContext} from "../../Contexts";
import {Link, matchRoutes, Outlet, useLocation} from "react-router-dom";
import {routes} from "../../routes";
import {Button, Layout, Menu} from "antd";
import * as styles from "./HomePage.module.css";
import ProfileMenu from "./ProfileMenu";
import {runInAction} from "mobx";
import {observer} from "mobx-react-lite";
import {Permission} from "../../domain/auth/Permission";
import {Feature} from "../../domain/Feature";
import AiCreditBadge from "./AiCreditBadge";
import QuickSearch from "./QuickSearch";

const SESSION_CHECK_INTERVAL_IN_MS = 5000;

const MainMenu = function ({items}: { items: any[] }) {
    const location = useLocation();
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const [selectedKeys, setSelectedKeys] = useState([]);

    useEffect(() => {
        const matchedRoutes = matchRoutes(routes, location.pathname);

        if (matchedRoutes != null) {
            const openMenuKey = matchedRoutes[1].route.handle?.menuOpenKey;
            const menuKey = matchedRoutes[1].route.handle?.menuKey;

            setSelectedKeys([menuKey]);

            setOpenKeys([openMenuKey]);
        } else {
            setSelectedKeys([]);
        }
    }, [location]);

    return <Menu className={"home-page__main-menu"}
                 theme="dark"
                 openKeys={openKeys}
                 onOpenChange={keys => setOpenKeys(keys)}
                 selectedKeys={selectedKeys}
                 mode="inline"
                 items={items}
    />;
};

const HomePage = observer(() => {
    const appContext = useContext(AppContextContext);
    const authService = useContext(AuthServiceContext);
    const clientNotificationService = useContext(ClientNotificationServiceContext);

    useLayoutEffect(() => {
        clientNotificationService.connectToServer(appContext.publicAccessUserId);

        return function cleanup() {
            clientNotificationService.disconnectFromServer();
        }
    }, [appContext.publicAccessUserId, clientNotificationService]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        checkSessionExpiresAt();

        const timerHandle = setInterval(checkSessionExpiresAt, SESSION_CHECK_INTERVAL_IN_MS);

        return function cleanup() {
            clearInterval(timerHandle);
        }
    }, []);

    const mainMenuItems: any[] = [];
    const canReadSbomProjects = appContext.user.hasFeature(Feature.SBOM)
        && appContext.user.hasOneOfPermission([
            Permission.SBOM_PROJECT__READ,
            Permission.SBOM_PROJECT__MANAGE,
            Permission.SBOM_PROJECT__MANAGE_ORG
        ]);

    // 1. Vulnerability items

    if (appContext.user.hasOneOfPermission([Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG])) {
        mainMenuItems.push({key: "vul-source-items", label: <Link to={"/vul-source-items"}>Vulnerability items</Link>});
    }

    // 2. Vulnerability reports

    if (appContext.user.hasPermission(Permission.REPORT__READ)) {
        mainMenuItems.push({key: "vul-reports", label: <Link to={"/vul-reports"}>Vulnerability reports</Link>});
    }

    // 3. Tickets

    if (appContext.user.hasPermission(Permission.TICKET__MANAGE)) {
        mainMenuItems.push({key: "tickets", label: <Link to={"/tickets"}>Tickets</Link>});
    }

    // 4. Inventory

    if (canReadSbomProjects) {
        const inventoryItems: any[] = [];

        if (!appContext.user.publicAccessAccount) {
            inventoryItems.push({key: "devices", label: <Link to={"/devices"}>Devices</Link>});
        }

        inventoryItems.push({key: "sbom-projects", label: <Link to={"/sbom-projects"}>Projects</Link>});

        if (appContext.user.hasPermission(Permission.SBOM_PROJECT__MANAGE_ORG)) {
            inventoryItems.push({key: "sbom-project-groups", label: <Link to={"/sbom-project-groups"}>Project groups</Link>});
        }

        inventoryItems.push({key: "sbom-components", label: <Link to={"/sbom-components"}>Components</Link>});

        if (appContext.user.hasPermission(Permission.SBOM_PROJECT__MANAGE_ORG)) {
            inventoryItems.push({key: "software-licenses", label: <Link to={"/software-licenses"}>Licenses</Link>});
            inventoryItems.push({key: "software-license-policies", label: <Link to={"/software-license-policies"}>License policies</Link>});
            inventoryItems.push({key: "software-license-overrides", label: <Link to={"/software-license-overrides"}>License overrides</Link>});
            inventoryItems.push({key: "sbom-report-definitions", label: <Link to={"/sbom-project-report-definitions"}>Report definitions</Link>});
        }

        mainMenuItems.push({key: "sbom", label: "Inventory", children: inventoryItems});
    }

    // 5. Definitions

    const definitionsMenuItems: any[] = [];

    if (appContext.user.hasOneOfPermission([Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG])) {
        definitionsMenuItems.push({key: "vul-sources", label: <Link to={"/vul-sources"}>Sources</Link>});
    }

    if (appContext.user.hasPermission(Permission.SOURCE__MANAGE_ORG)) {
        definitionsMenuItems.push({key: "vul-source-health-report-definitions", label: <Link to={"/vul-source-health-report-definitions"}>Source health reports</Link>});
    }

    if (appContext.user.hasPermission(Permission.ATTRIBUTE__READ)) {
        definitionsMenuItems.push({key: "attributes/definitions", label: <Link to={"/attributes/definitions"}>Attributes</Link>});
    }

    if (appContext.user.hasOneOfPermission([Permission.VIEW__READ_SEAL, Permission.VIEW__READ_ORG])) {
        definitionsMenuItems.push({key: "vul-source-items/views", label: <Link to={"/vul-source-items/views"}>Vulnerability views</Link>});
    }

    if (appContext.user.hasPermission(Permission.REPORT__MANAGE)) {
        definitionsMenuItems.push({key: "vul-reports/definitions", label: <Link to={"/definitions/vul-reports"}>Vulnerability reports</Link>});
    }

    if (appContext.user.hasOneOfPermission([Permission.ITEM_UPDATE_PROFILE__READ_SEAL, Permission.ITEM_UPDATE_PROFILE__READ_ORG, Permission.ITEM_UPDATE_PROFILE__MANAGE_ORG])) {
        definitionsMenuItems.push({key: "item-update-profiles", label: <Link to={"/definitions/item-update-profiles"}>Item update profiles</Link>});
    }

    if (definitionsMenuItems.length > 0) {
        mainMenuItems.push({key: "definitions", label: "Definitions", children: definitionsMenuItems});
    }

    // 6. AI

    const aiMenuItems: any[] = [];
    const organizationHasAiFeature = appContext.user.features?.includes(Feature.AI);

    if (appContext.user.hasFeature(Feature.AI) && appContext.user.hasOneOfPermission([Permission.INTEREST__READ_ORG, Permission.INTEREST__MANAGE_ORG])) {
        aiMenuItems.push({key: "interests", label: <Link to={"/interests"}>Interests</Link>});
    }

    if (appContext.user.hasFeature(Feature.AI) && appContext.user.hasOneOfPermission([Permission.AI_ATTRIBUTE__READ_SEAL, Permission.AI_ATTRIBUTE__READ_ORG, Permission.AI_ATTRIBUTE__MANAGE_ORG])) {
        aiMenuItems.push({key: "ai-attributes", label: <Link to={"/definitions/ai-attributes"}>AI attributes</Link>});
    }

    const canOpenAiPrompts = appContext.user.isSystemAdmin()
        || (appContext.user.hasFeature(Feature.AI)
            && appContext.user.hasOneOfPermission([
                Permission.AI_PROMPT__READ_SEAL,
                Permission.AI_PROMPT__READ_ORG,
                Permission.AI_PROMPT__MANAGE_ORG
            ]));

    if (canOpenAiPrompts) {
        aiMenuItems.push({key: "ai-prompts", label: <Link to={"/definitions/ai-prompts"}>AI prompts</Link>});
    }

    if (appContext.user.hasFeature(Feature.AI) && appContext.user.hasOneOfPermission([Permission.AI_REPORT_SUMMARIZER__READ_SEAL, Permission.AI_REPORT_SUMMARIZER__READ_ORG, Permission.AI_REPORT_SUMMARIZER__MANAGE_ORG])) {
        aiMenuItems.push({key: "ai-report-summarizers", label: <Link to={"/definitions/ai-report-summarizers"}>AI report summarizers</Link>});
    }

    if (appContext.user.hasFeature(Feature.AI) && appContext.user.hasOneOfPermission([Permission.AI_EXTRACTOR__READ_SEAL, Permission.AI_EXTRACTOR__READ_ORG, Permission.AI_EXTRACTOR__MANAGE_ORG])) {
        aiMenuItems.push({key: "ai-extractors", label: <Link to={"/definitions/ai-extractors"}>AI extractors</Link>});
    }

    if (appContext.user.isSystemAdmin() || (organizationHasAiFeature && appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG))) {
        aiMenuItems.push({key: "ai-settings", label: <Link to={"/ai-settings"}>Settings</Link>});
    }

    if (aiMenuItems.length > 0) {
        mainMenuItems.push({key: "ai", label: "AI", children: aiMenuItems});
    }

    // 7. Analytics

    const reportsMenuItems: any[] = [];

    if (appContext.user.hasFeature(Feature.AI) && appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG)) {
        reportsMenuItems.push({key: "ai-usage", label: <Link to={"/ai-usage"}>AI usage</Link>});
    }

    if (appContext.user.hasOneOfPermission([Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG])) {
        reportsMenuItems.push({key: "source-updates", label: <Link to={"/source-updates"}>Source updates</Link>});
        reportsMenuItems.push({key: "attribute-changes", label: <Link to={"/attribute-changes"}>Attribute changes</Link>});
    }

    if (reportsMenuItems.length > 0) {
        mainMenuItems.push({key: "reports", label: "Analytics", children: reportsMenuItems});
    }

    // 8. Administration (user management + AI admin + system admin)

    const adminMenuItems: any[] = [];
    const userManagementMenuItems: any[] = [];
    const adminAiMenuItems: any[] = [];
    const systemMenuItems: any[] = [];

    if (appContext.user.hasPermission(Permission.USER__MANAGE)) {
        userManagementMenuItems.push(
            {key: "users", label: <Link to={"/users"}>Users</Link>},
            {key: "api-tokens", label: <Link to={"/api-tokens"}>API tokens</Link>},
            {key: "user-roles", label: <Link to={"/roles"}>User roles</Link>}
        );
    }

    if (appContext.user.isSystemAdmin()) {
        userManagementMenuItems.push({key: "organizations", label: <Link to={"/organizations"}>Organizations</Link>});
    }

    if (appContext.user.hasFeature(Feature.AI) && appContext.user.isSystemAdmin()) {
        adminAiMenuItems.push({key: "ai-provider-connections", label: <Link to={"/definitions/ai-provider-connections"}>AI provider connections</Link>});
    }

    if (appContext.user.isSystemAdmin()) {
        adminAiMenuItems.push(
            {key: "ai-model-pricing", label: <Link to={"/definitions/ai-model-pricing"}>AI model credits</Link>},
            {key: "external-search-pricing", label: <Link to={"/definitions/external-search-pricing"}>External search credits</Link>},
            {key: "ai-tiers", label: <Link to={"/definitions/ai-tiers"}>AI tiers</Link>},
            {key: "ai-defaults", label: <Link to={"/administration/ai/defaults"}>AI defaults</Link>}
        );
    }

    if (appContext.user.isSystemAdmin()) {
        systemMenuItems.push(
            {key: "audit-logs", label: <Link to={"/audit-logs"}>Audit logs</Link>},
            {key: "queues", label: <Link to={"/queues"}>Queues</Link>},
            {key: "import-export", label: <Link to={"/import-export"}>Import / Export</Link>}
        );
    }

    if (userManagementMenuItems.length > 0) {
        adminMenuItems.push({key: "user-management", label: "User management", type: "group", children: userManagementMenuItems});
    }

    if (adminAiMenuItems.length > 0) {
        adminMenuItems.push({key: "admin-ai", label: "AI", type: "group", children: adminAiMenuItems});
    }

    if (systemMenuItems.length > 0) {
        adminMenuItems.push({key: "system", label: "System", type: "group", children: systemMenuItems});
    }

    if (adminMenuItems.length > 0) {
        mainMenuItems.push({key: "administration", label: "Administration", children: adminMenuItems});
    }

    return <Layout className={styles.layout}>
        {!appContext.presentationMode && <Layout.Header className={styles.header}>
            <div className={styles.logo}>
                <img src="/img/logo.svg" alt={"SEAL"}/>
            </div>

            {!appContext.publicAccess && <QuickSearch/>}

            <div className={styles.center}/>

            <div className={styles.toolbar}>
                {!appContext.publicAccess && <>
                    <AiCreditBadge/>
                    <ProfileMenu/>
                </>}
            </div>
        </Layout.Header>}
        <Layout hasSider={!appContext.presentationMode}>
            {!appContext.presentationMode && <Layout.Sider width={220} className={styles.sider}>
                <MainMenu items={mainMenuItems}/>
            </Layout.Sider>}
            <Layout.Content className={styles.content}>
                <Outlet/>

                {appContext.presentationMode && <ExitPresentationMode/>}
            </Layout.Content>
        </Layout>
    </Layout>;

    function checkSessionExpiresAt() {
        if (appContext.jwtTokenExpiresAt) {
            // pokud předpokládáme že sezení pozbylo platnosti, pošleme dotaz na server pro informaci o přihlášeném uživateli
            // - pokud uživatel není přihlášen (sezení není platné), provede Axios interceptor automaticky odhlášení na frontendu
            if (appContext.jwtTokenExpiresAt.getTime() < (new Date().getTime() + SESSION_CHECK_INTERVAL_IN_MS)) {
                authService.getLoggedInUser();
            }
        }
    }
});

function ExitPresentationMode() {
    const appContext = useContext(AppContextContext);

    return <>
        <Button className={styles['exit-presentation-mode']}
                onClick={() => runInAction(() => appContext.presentationMode = false)}
                type={"primary"}>
            Exit presentation mode
        </Button>
    </>;
}

export default HomePage;
